import { Injectable } from '@angular/core';
import { MojoShareResourceService, Share } from 'mojo-share-resource';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { TeamMember, TeamMembers, TeamMembersListItem, TeamRole, User } from '@shared';
import { User as MojoUser } from 'mojo-share-resource/lib/models/mojo-team-members';
import {
  convertMojoUserToTeamMember,
  convertTeamMemberListItemsToTeamMembers,
} from '@modules/jobs-page/utils/utils';
import {
  getJobsSelectedId,
  getSortedTeamMembersByLastModifiedDate,
  JobsTeamMembersUpdate,
} from '@collections/jobs';
import {
  SharedResources,
  SharedResourcesType,
} from '@core/shared-resources/shared-resources.model';
import {
  sharedResourcesRemove,
  sharedResourcesAdd,
  SharedResourcesLoadedObj,
} from 'mojo-share-resource';

import { sharedResourcesStoreCurrent } from '@core/shared-resources/store/shared-resources.actions';
import { getCurrentSharedResource } from '../store/shared-resources.selectors';

@Injectable()
export class SharedResourcesService implements MojoShareResourceService {
  constructor(private store: Store<{}>) {}

  getShares(resourceId: number, resourceType?: string): Observable<Share[]> {
    return this.getSortedTeamMembersListItems(resourceId, resourceType).pipe(
      map((teamMemberList) =>
        teamMemberList.map((teamMember: TeamMembersListItem) => ({
          id: teamMember.user.id,
          user: teamMember.user as MojoUser,
          accessLevel: teamMember.role,
        })),
      ),
    );
  }

  removeShare(id: number, resourceType?: string): Observable<{}> {
    return this.store.select(getCurrentSharedResource).pipe(
      take(1),
      switchMap((currentSharedResource: SharedResources) => {
        switch (resourceType) {
          case SharedResourcesType.job: {
            return this.removeJobShares(id);
          }
          case SharedResourcesType.talentPool: {
            this.removeSharedResource(
              id,
              currentSharedResource.resourceId,
              currentSharedResource.resourceType,
            );
            return of({});
          }
          case SharedResourcesType.note: {
            const teamMembers: TeamMember[] = currentSharedResource.teamMembers.filter(
              (teamMember) => teamMember.id !== id,
            );
            this.storeResource(
              currentSharedResource.resourceId,
              currentSharedResource.resourceType,
              teamMembers,
            );
            return of({});
          }
        }
      }),
    );
  }

  save(
    users: MojoUser[],
    accessLevelId: string,
    resourceId: number,
    resourceType?: string,
  ): Observable<TeamMembers> {
    return this.store.select(getCurrentSharedResource).pipe(
      take(1),
      switchMap((currentSharedResource) => {
        const updatedTeamMembers = users.map((user) => convertMojoUserToTeamMember(user));
        updatedTeamMembers.push(...currentSharedResource.teamMembers);
        const teamMembersObject = {
          hiringManagers: [],
          recruitingManagers: updatedTeamMembers,
          regularRecruiters: [],
          coordinators: [],
        };
        switch (resourceType) {
          case SharedResourcesType.job: {
            return this.saveJobTeamMembers(users, accessLevelId, resourceId);
          }
          case SharedResourcesType.talentPool: {
            const teamMemberIds = users.map((user) => +user.id);
            teamMemberIds.push(
              ...currentSharedResource.teamMembers?.map((teamMember) => teamMember.id),
            );
            this.addSharedResource(resourceId, resourceType, teamMemberIds);
            return of(teamMembersObject);
          }
          case SharedResourcesType.note: {
            this.storeResource(resourceId, resourceType, updatedTeamMembers);
            return of(teamMembersObject);
          }
        }
      }),
    );
  }

  updateAccess(
    userWithAccess: any,
    currentResourceId?: string | number,
    resourceType?: string,
  ): Observable<[TeamMembers, number]> {
    return this.getTeamMembers().pipe(
      map((teamMembers) => {
        const teamMemberIndex = teamMembers.findIndex(
          (teamMember) => teamMember.user.id === userWithAccess.id,
        );
        if (teamMemberIndex !== -1) {
          const updatedTeamMember: TeamMembersListItem = {
            user: userWithAccess.user,
            role: userWithAccess.accessLevel,
          };
          teamMembers.splice(teamMemberIndex, 1, updatedTeamMember);
        }
        return teamMembers;
      }),
      map((teamMemberListItems) => convertTeamMemberListItemsToTeamMembers(teamMemberListItems)),
      withLatestFrom(this.store.select(getJobsSelectedId)),
      tap(([teamMembers, resourceId]: [TeamMembers, number]) =>
        this.dispatchJobTeamMembersUpdate(teamMembers, resourceId),
      ),
      take(1),
    );
  }

  private getSortedTeamMembersListItems(
    resourceId,
    resourceType,
  ): Observable<TeamMembersListItem[]> {
    const rType = resourceType ? resourceType : SharedResourcesType.job;
    return this.store.select(
      SharedResourcesLoadedObj.getLoadedSharedResourcesAsSortedTeamMembersListByTypeAndId(
        rType,
        resourceId,
      ),
    );
  }

  private saveJobTeamMembers(
    users: MojoUser[],
    accessLevelId: string,
    resourceId: number,
  ): Observable<TeamMembers> {
    return this.getTeamMembers().pipe(
      map((currentTeamMembers: TeamMembersListItem[]) => {
        const newTeamMembers: TeamMembersListItem[] = users.map((user) => ({
          user: user as User,
          role: accessLevelId as TeamRole,
        }));
        return [...currentTeamMembers, ...newTeamMembers];
      }),
      map((teamMembersListItems: TeamMembersListItem[]) =>
        convertTeamMemberListItemsToTeamMembers(teamMembersListItems),
      ),
      tap((teamMembers) => this.dispatchJobTeamMembersUpdate(teamMembers, resourceId)),
      take(1),
    );
  }

  private removeJobShares(id: string | number): Observable<{}> {
    return this.getTeamMembers().pipe(
      map((teamMembers) => teamMembers.filter((teamMember) => teamMember.user.id !== id)),
      map(convertTeamMemberListItemsToTeamMembers),
      withLatestFrom(this.store.select(getJobsSelectedId)),
      tap(([teamMembers, resourceId]: [TeamMembers, number]) =>
        this.dispatchJobTeamMembersUpdate(teamMembers, resourceId),
      ),
      take(1),
    );
  }

  private dispatchJobTeamMembersUpdate(teamMembers: TeamMembers, resourceId: number) {
    return this.store.dispatch(
      new JobsTeamMembersUpdate({
        id: resourceId,
        members: teamMembers,
      }),
    );
  }

  private getTeamMembers(): Observable<TeamMembersListItem[]> {
    return this.store.pipe(
      select(getSortedTeamMembersByLastModifiedDate),
      filter(
        (teamMembers: TeamMembersListItem[]) => !teamMembers.some((teamMember) => !teamMember.user),
      ),
    );
  }

  private storeResource(resourceId, resourceType: string, teamMembers: TeamMember[]) {
    this.store.dispatch(
      sharedResourcesStoreCurrent({
        resourceId: resourceId ? resourceId : undefined,
        resourceType: resourceType as SharedResourcesType,
        teamMembers,
      }),
    );
  }

  private removeSharedResource(userId: number, resourceId: number, resourceType: string) {
    this.store.dispatch(
      sharedResourcesRemove({
        userId,
        resourceId,
        resourceType: resourceType as SharedResourcesType,
      }),
    );
  }

  private addSharedResource(resourceId: number, resourceType: string, teamMemberIds: number[]) {
    this.store.dispatch(
      sharedResourcesAdd({
        resourceId,
        resourceType: resourceType as SharedResourcesType,
        teamMemberIds,
      }),
    );
  }
}
